import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Test, Question } from '../../types/test';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import AssignTestModal from '../../components/AssignTestModal';
import './TestEditor.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TestEditor: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  useAuth(); // Auth context used for authentication state
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [showAddQuestionMenu, setShowAddQuestionMenu] = useState(false);
  const [showEndTestModal, setShowEndTestModal] = useState(false);
  const [showDeleteTestModal, setShowDeleteTestModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [generatingOptions, setGeneratingOptions] = useState<string | null>(null); // Track which question is generating
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    if (testId) {
      fetchTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.add-question-dropdown')) {
        setShowAddQuestionMenu(false);
      }
    };

    if (showAddQuestionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAddQuestionMenu]);

  const fetchTest = async () => {
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTest(data.test);
        if (data.test.dueDate) {
          setDueDate(data.test.dueDate);
        }
      }
    } catch (error) {
      console.error('Error fetching test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!test) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify(test),
      });
      if (response.ok) {
        setModal({
          isOpen: true,
          title: 'Success',
          message: 'Test saved successfully!',
          type: 'success',
        });
      } else {
        throw new Error('Failed to save test');
      }
    } catch (error) {
      console.error('Error saving test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save test. Please try again.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!test) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/tests/${testId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          dueDate: dueDate || undefined,
        }),
      });
      if (response.ok) {
        setModal({
          isOpen: true,
          title: 'Success',
          message: 'Test published successfully!',
          type: 'success',
        });
        fetchTest();
      } else {
        throw new Error('Failed to publish test');
      }
    } catch (error) {
      console.error('Error publishing test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to publish test. Please try again.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (questionId: string, updates: Partial<Question>) => {
    if (!test) return;
    setTest({
      ...test,
      questions: test.questions.map((q) =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    });
  };

  const addQuestion = (type: 'multiple-choice' | 'true-false' = 'multiple-choice') => {
    if (!test) return;
    const newQuestion: Question = {
      id: `temp-${Date.now()}-${Math.random()}`,
      questionText: '',
      type,
      points: 10,
      options: type === 'multiple-choice' ? ['', '', '', ''] : undefined,
      correctAnswer: type === 'true-false' ? 'True' : undefined,
    };
    setTest({
      ...test,
      questions: [...test.questions, newQuestion],
    });
    setShowAddQuestionMenu(false);
  };

  const removeQuestion = (questionId: string) => {
    if (!test) return;
    if (test.questions.length <= 1) {
      setModal({
        isOpen: true,
        title: 'Cannot Remove Question',
        message: 'A test must have at least one question.',
        type: 'warning',
      });
      return;
    }
    setTest({
      ...test,
      questions: test.questions.filter((q) => q.id !== questionId),
    });
  };

  const moveQuestion = (questionId: string, direction: 'up' | 'down') => {
    if (!test) return;
    const currentIndex = test.questions.findIndex((q) => q.id === questionId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= test.questions.length) return;

    const newQuestions = [...test.questions];
    [newQuestions[currentIndex], newQuestions[newIndex]] = [
      newQuestions[newIndex],
      newQuestions[currentIndex],
    ];
    setTest({
      ...test,
      questions: newQuestions,
    });
  };

  const generateOptions = async (questionId: string) => {
    if (!test) return;
    const question = test.questions.find(q => q.id === questionId);
    if (!question || !question.questionText.trim()) {
      setModal({
        isOpen: true,
        title: 'Question Text Required',
        message: 'Please enter a question before generating options.',
        type: 'warning',
      });
      return;
    }

    setGeneratingOptions(questionId);
    try {
      const response = await fetch(`${API_URL}/questions/generate-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          questionText: question.questionText,
          questionType: question.type,
          numOptions: 4,
          topicContext: test.topic || test.title, // Pass test topic for better context
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate options');
      }

      const data = await response.json();
      
      if (question.type === 'true-false') {
        updateQuestion(questionId, {
          correctAnswer: data.correctAnswer,
        });
      } else {
        updateQuestion(questionId, {
          options: data.options,
          correctAnswer: data.correctAnswer,
        });
      }

      setModal({
        isOpen: true,
        title: 'Options Generated',
        message: data.explanation || 'Answer options have been generated successfully.',
        type: 'success',
      });
    } catch (error) {
      console.error('Error generating options:', error);
      setModal({
        isOpen: true,
        title: 'Generation Failed',
        message: 'Failed to generate options. Please try again or enter them manually.',
        type: 'error',
      });
    } finally {
      setGeneratingOptions(null);
    }
  };

  const handleEndTest = async () => {
    if (!test) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          ...test,
          published: false,
        }),
      });
      if (response.ok) {
        setModal({
          isOpen: true,
          title: 'Success',
          message: 'Test has been ended and is no longer available to employees.',
          type: 'success',
        });
        setShowEndTestModal(false);
        fetchTest();
      } else {
        throw new Error('Failed to end test');
      }
    } catch (error) {
      console.error('Error ending test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to end test. Please try again.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTest = async () => {
    if (!test) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        setModal({
          isOpen: true,
          title: 'Success',
          message: 'Test has been deleted successfully.',
          type: 'success',
        });
        setShowDeleteTestModal(false);
        // Navigate back to test creation page after a short delay
        setTimeout(() => {
          navigate('/admin/tests/create');
        }, 1500);
      } else {
        throw new Error('Failed to delete test');
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete test. Please try again.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading test...</div>;
  }

  if (!test) {
    return <div className="error-container">Test not found</div>;
  }

  // Check if test is expired (past due date)
  const isExpired = test.dueDate ? new Date(test.dueDate) < new Date() : false;
  const isReadOnly = test.published || isExpired;

  return (
    <div className="test-editor">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <Modal
        isOpen={showEndTestModal}
        onClose={() => setShowEndTestModal(false)}
        title="End Test"
        message="Are you sure you want to end this test? Employees will no longer be able to take it, but existing results will be preserved."
        type="warning"
        showCancel={true}
        confirmText="End Test"
        onConfirm={handleEndTest}
      />
      
      <Modal
        isOpen={showDeleteTestModal}
        onClose={() => setShowDeleteTestModal(false)}
        title="Delete Test"
        message="Are you sure you want to delete this test? This action cannot be undone. All test results will also be deleted."
        type="error"
        showCancel={true}
        confirmText="Delete"
        onConfirm={handleDeleteTest}
      />
      
      <div className="editor-header">
        <button className="button-back" onClick={() => navigate('/admin/tests/create')}>
          ← Back to Tests
        </button>
        <div className="editor-title">
          <h1>{test.title}</h1>
          <p>{test.description}</p>
        </div>
      </div>

      <div className="questions-list">
        <div className="questions-list-header">
          <h2>Questions ({test.questions.length})</h2>
          {!isReadOnly && (
            <div className="add-question-dropdown">
              <button 
                className="button-secondary add-question-btn"
                onClick={() => setShowAddQuestionMenu(!showAddQuestionMenu)}
              >
                + Add Question
              </button>
              {showAddQuestionMenu && (
                <div className="add-question-menu">
                  <button onClick={() => addQuestion('multiple-choice')}>
                    Multiple Choice
                  </button>
                  <button onClick={() => addQuestion('true-false')}>
                    True/False
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {test.questions.map((question, index) => (
          <div key={question.id} className="question-card">
            <div className="question-header">
              <div className="question-header-left">
                <span className="question-number">Q{index + 1}</span>
                <div className="question-points-editor">
                  <label className="points-label">Points:</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={question.points}
                    onChange={(e) => {
                      const points = parseInt(e.target.value) || 1;
                      updateQuestion(question.id, { points: Math.max(1, points) });
                    }}
                    className="points-input"
                    disabled={isReadOnly}
                  />
                </div>
                {!isReadOnly && (
                  <div className="question-actions">
                    <button
                      type="button"
                      className="question-action-btn"
                      onClick={() => moveQuestion(question.id, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="question-action-btn"
                      onClick={() => moveQuestion(question.id, 'down')}
                      disabled={index === test.questions.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="question-action-btn delete-btn"
                      onClick={() => removeQuestion(question.id)}
                      title="Delete question"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="question-type-selector">
              <label className="field-label">Question Type:</label>
              <select
                value={question.type}
                onChange={(e) => {
                  const newType = e.target.value as Question['type'];
                  const updates: Partial<Question> = { type: newType };
                  if (newType === 'multiple-choice') {
                    updates.options = ['', '', '', ''];
                    updates.correctAnswer = undefined;
                  } else if (newType === 'true-false') {
                    updates.options = undefined;
                    updates.correctAnswer = 'True';
                  }
                  updateQuestion(question.id, updates);
                }}
                className="question-type-select"
                disabled={isReadOnly}
              >
                <option value="multiple-choice">Multiple Choice</option>
                <option value="true-false">True/False</option>
              </select>
            </div>
            <textarea
              className="question-text"
              value={question.questionText}
              onChange={(e) =>
                updateQuestion(question.id, { questionText: e.target.value })
              }
              placeholder="Enter question text..."
              disabled={isReadOnly}
            />
            {question.type === 'multiple-choice' && question.options && (
              <div className="options-list">
                <div className="options-list-header">
                  <label className="field-label">Answer Options (click to set as correct answer):</label>
                  {!isReadOnly && (
                    <div className="options-header-buttons">
                      <button
                        type="button"
                        className="button-ai generate-options-btn"
                        onClick={() => generateOptions(question.id)}
                        disabled={generatingOptions === question.id || !question.questionText.trim()}
                        title="Use AI to generate answer options"
                      >
                        {generatingOptions === question.id ? (
                          <>
                            <span className="generating-spinner"></span>
                            Generating...
                          </>
                        ) : (
                          <>
                            ✨ Generate Options
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="button-secondary small-btn"
                        onClick={() => {
                          const newOptions = [...(question.options || []), ''];
                          updateQuestion(question.id, { options: newOptions });
                        }}
                      >
                        + Add Option
                      </button>
                    </div>
                  )}
                </div>
                {question.options.map((option, optIndex) => {
                  // Check if this option is the correct answer (case-insensitive comparison)
                  // Only mark as correct if both option and correctAnswer are non-empty
                  const correctAnswer = question.correctAnswer || '';
                  const optionValue = option.trim();
                  const correctValue = correctAnswer.toString().trim();
                  const isCorrect = optionValue !== '' && correctValue !== '' && 
                                    optionValue.toLowerCase() === correctValue.toLowerCase();
                  return (
                    <div key={optIndex} className="option-row">
                      <button
                        type="button"
                        className={`option-select-btn ${isCorrect ? 'selected-correct' : ''}`}
                        onClick={() => {
                          // Set this option as the correct answer
                          updateQuestion(question.id, { correctAnswer: option });
                        }}
                        title={isCorrect ? 'Correct Answer (click to deselect)' : `Click to set as correct answer`}
                        disabled={isReadOnly}
                      >
                        {isCorrect ? '✓' : '○'}
                      </button>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...question.options!];
                          newOptions[optIndex] = e.target.value;
                          // If this was the correct answer, update the correctAnswer to match the new text
                          let updatedCorrectAnswer = question.correctAnswer;
                          if (isCorrect) {
                            updatedCorrectAnswer = e.target.value;
                          }
                          updateQuestion(question.id, { 
                            options: newOptions,
                            correctAnswer: updatedCorrectAnswer
                          });
                        }}
                        placeholder={`Option ${optIndex + 1}`}
                        className={`option-input ${isCorrect ? 'option-correct' : ''}`}
                        title={isCorrect ? 'Correct Answer' : `Option ${optIndex + 1}`}
                        disabled={isReadOnly}
                      />
                      {question.options && question.options.length > 2 && !isReadOnly && (
                        <button
                          type="button"
                          className="remove-option-btn"
                          onClick={() => {
                            const newOptions = question.options!.filter((_, i) => i !== optIndex);
                            // If the removed option was the correct answer, clear it
                            let updatedCorrectAnswer = question.correctAnswer;
                            if (isCorrect) {
                              updatedCorrectAnswer = undefined;
                            }
                            updateQuestion(question.id, { 
                              options: newOptions,
                              correctAnswer: updatedCorrectAnswer
                            });
                          }}
                          title="Remove option (minimum 2 required)"
                        >
                          ×
                        </button>
                      )}
                      {question.options && question.options.length <= 2 && (
                        <span className="min-options-note" title="Minimum 2 options required">
                          (min)
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {question.type === 'true-false' && (
              <div className="correct-answer-section">
                <div className="correct-answer-header">
                  <label className="field-label">Correct Answer:</label>
                  {!isReadOnly && (
                    <button
                      type="button"
                      className="button-ai generate-options-btn small"
                      onClick={() => generateOptions(question.id)}
                      disabled={generatingOptions === question.id || !question.questionText.trim()}
                      title="Use AI to determine the correct answer"
                    >
                      {generatingOptions === question.id ? (
                        <>
                          <span className="generating-spinner"></span>
                          Checking...
                        </>
                      ) : (
                        <>
                          ✨ Auto-detect Answer
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div className="true-false-options">
                  <button
                    type="button"
                    className={`true-false-btn ${question.correctAnswer?.toString().toLowerCase() === 'true' ? 'selected-correct' : ''}`}
                    onClick={() => updateQuestion(question.id, { correctAnswer: 'True' })}
                    disabled={isReadOnly}
                  >
                    True
                  </button>
                  <button
                    type="button"
                    className={`true-false-btn ${question.correctAnswer?.toString().toLowerCase() === 'false' ? 'selected-correct' : ''}`}
                    onClick={() => updateQuestion(question.id, { correctAnswer: 'False' })}
                    disabled={isReadOnly}
                  >
                    False
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="editor-actions-bottom">
        {!isReadOnly && (
          <div className="publish-schedule">
            <label>
              Due Date (optional):
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
          </div>
        )}
        {isReadOnly && (test.published || isExpired) && (
          <div className="read-only-notice">
            {isExpired ? (
              <p className="notice-text">⚠️ This test has expired and cannot be edited.</p>
            ) : (
              <p className="notice-text">⚠️ This test is published and cannot be edited. Use "End Test" to unpublish it first.</p>
            )}
          </div>
        )}
        <div className="editor-buttons">
          {!isReadOnly ? (
            <>
              <button
                className="button-secondary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="button-primary"
                onClick={handlePublish}
                disabled={saving}
              >
                Publish Test
              </button>
            </>
          ) : (
            <>
              <button
                className="button-assign"
                onClick={() => setShowAssignModal(true)}
                disabled={saving}
              >
                📧 Assign to Employees
              </button>
              <button
                className="button-secondary"
                onClick={() => setShowEndTestModal(true)}
                disabled={saving}
              >
                End Test
              </button>
              <button
                className="button-danger"
                onClick={() => setShowDeleteTestModal(true)}
                disabled={saving}
              >
                Delete Test
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Assign Test Modal */}
      {test && testId && (
        <AssignTestModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          testId={testId}
          testTitle={test.title}
          onAssigned={() => {
            setModal({
              isOpen: true,
              title: 'Success',
              message: 'Test assigned successfully! Email notifications have been sent.',
              type: 'success',
            });
          }}
        />
      )}
    </div>
  );
};

export default TestEditor;
